export type SidebarNavigationSectionLabel = {
  /** The title of the link section. */
  title: string;
};

export type SidebarNavigationLink = {
  /** The title of the link */
  title: string;
  /** The href of the link. Can be absolute, relative or external. */
  href: string;
  /**
   * Whether or not the href needs to match exactly to the current url path or only start with it.
   * Useful mostly for index pages like `/` that should not be considered active when the current path is `/foo`.
   */
  exactMatch?: boolean;
  /** ISO 8601 date string from markdown frontmatter, stamped at build time. */
  lastUpdated?: string;
};

export type SidebarNavigationLinkGroup = {
  title: string;
  /** Optional href — makes the group title itself a clickable link. */
  href?: string;
  children: SidebarNavigationLinkOrGroup[];
};

export type SidebarNavigationLinkOrGroup =
  | SidebarNavigationLink
  | SidebarNavigationLinkGroup;

export type SidebarNavigationStructure = (
  | SidebarNavigationSectionLabel
  | SidebarNavigationLinkOrGroup
)[];

export function isNavigationLink(
  item: SidebarNavigationSectionLabel | SidebarNavigationLinkOrGroup,
): item is SidebarNavigationLink {
  return "href" in item && !("children" in item);
}

export function isNavigationLinkGroup(
  item: SidebarNavigationSectionLabel | SidebarNavigationLinkOrGroup,
): item is SidebarNavigationLinkGroup {
  return "children" in item;
}

export function isNavigationSectionLabel(
  item: SidebarNavigationSectionLabel | SidebarNavigationLinkOrGroup,
): item is SidebarNavigationSectionLabel {
  return !("children" in item) && !("href" in item);
}

export function isSidebarNavigationItemActive(
  item: SidebarNavigationSectionLabel | SidebarNavigationLinkOrGroup,
  currentPath: string,
): boolean {
  if (isNavigationLink(item)) {
    return item.exactMatch
      ? item.href === currentPath
      : currentPath.startsWith(item.href);
  }

  if (isNavigationLinkGroup(item)) {
    if (item.href && currentPath.startsWith(item.href)) return true;
    return item.children.some((child) =>
      isSidebarNavigationItemActive(child, currentPath),
    );
  }

  return false;
}

export function isExternalUrl(url: string): boolean {
  return url.startsWith("http://") || url.startsWith("https://");
}
